import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {
  DomainSetting,
  DomainTrackingClientConfig,
  DomainTrackingConfig,
  DomainTrackingServerConfig,
} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeInputV2Component} from '../../gatewee-input-v2/gatewee-input-v2.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
}

@Component({
  selector: 'tracking-parameter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeInputV2Component,
  ],
  templateUrl: './tracking-parameter.component.html',
})
export class TrackingParameterComponent implements OnInit {
  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.setting = this.context.data.setting;
    this.editSettingFn = this.context.data.editSettingFn;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private parseValue(): DomainTrackingConfig {
    const raw = this.setting.value;
    if (!raw) return {};
    try {
      const parsed =
        typeof raw === 'string'
          ? JSON.parse(raw)
          : (raw as DomainTrackingConfig);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  }

  private initializeForm(): void {
    const config = this.parseValue();
    const client = config.client ?? {};
    const server = config.server ?? {};

    const clientGroup = this.fb.group({
      ga4MeasurementId: [client.ga4MeasurementId ?? ''],
      metaPixelId: [client.metaPixelId ?? ''],
      tiktokPixelId: [client.tiktokPixelId ?? ''],
    });
    const serverGroup = this.fb.group({
      ga4ApiSecret: [server.ga4ApiSecret ?? ''],
      metaConversionsApiToken: [server.metaConversionsApiToken ?? ''],
      tiktokEventsApiToken: [server.tiktokEventsApiToken ?? ''],
    });

    this.formGroup = this.fb.group({
      client: clientGroup,
      server: serverGroup,
    });
  }

  onSubmit(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.submitting = true;
    const clientVal = this.formGroup.get('client')
      ?.value as DomainTrackingClientConfig;
    const serverVal = this.formGroup.get('server')
      ?.value as DomainTrackingServerConfig;

    const value: DomainTrackingConfig = {
      client: this.trimEmpty(clientVal),
      server: this.trimEmpty(serverVal),
    };

    const updatedSetting = {
      ...this.setting,
      value,
    } as RecipientSetting | DomainSetting;

    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: (error: Error) => {
        console.error('Error updating tracking setting:', error);
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  private trimEmpty<T extends object>(obj: T): T | undefined {
    const out = {...obj} as Record<string, unknown>;
    for (const k of Object.keys(out)) {
      const v = out[k];
      if (v === '' || v === null || v === undefined) delete out[k];
    }
    return (Object.keys(out).length ? out : undefined) as T | undefined;
  }

  close(): void {
    this.context.completeWith(false);
  }
}
